import { http } from './http';
import type { ResellerProfile } from './types/profile';
import type {
    ResellerOrder,
    ResellerOrderResponse,
    ResellerOrderCount,
    ResellerProduct,
    ResellerProductPrice,
    ResellerCheckoutInfo,
    CheckoutResponse,
    QuoteCheckoutInfo,
    ResellerExtraSupport,
} from './types/reseller';
import type { HttpResponse } from './http';
import type { Country } from './types/helper';
import type { CheckoutCreateRequest, CheckoutCreateResult } from './cart.service';
import { secureServer } from './AppSettings';

export const resellerService = {
    emptyProfile,
    getProfile,
    updateProfile,
    updatePassword,
    getOrders,
    getOrdersCount,
    getProducts,
    getProductPrice,
    getCountries,
    getCheckoutInfo,
    createCheckout,
    checkout,
    getQuoteCheckoutInfo,
    getExtraSupportInfo,
};

function emptyProfile(): ResellerProfile {
    return {
        login: '',
        reseller: '',
        resellerEmail: '',
        firstName: '',
        lastName: '',
        company: '',
        address: '',
        city: '',
        state: '',
        country: '',
        zip: '',
        phone: '',
        fax: '',
    };
}

async function getProfile(resellerId: number): Promise<ResellerProfile | null> {
    const result = await http<ResellerProfile>({
        path: `/resellers/${resellerId}/profile`,
    });
    if (result.ok) {
        const profile = result.body;
        return profile;
    } else {
        return null;
    }
}

async function updateProfile(resellerId: number, data: ResellerProfile): Promise<boolean> {
    const result = await http<any, ResellerProfile>({
        path: `/resellers/${resellerId}/profile`,
        method: 'PUT',
        body: data,
    });
    return result.ok;
}

export interface UpdatePasswordRequest {
    oldPassword: string;
    newPassword: string;
}

async function updatePassword(resellerId: number, data: UpdatePasswordRequest): Promise<boolean> {
    const result = await http<any, UpdatePasswordRequest>({
        path: `/resellers/${resellerId}/password`,
        method: 'POST',
        body: data,
    });
    return result.ok;
}

const mapResellerOrder = (order: ResellerOrderResponse): ResellerOrder => ({
    ...order,
    dateTime: new Date(order.dateTime),
    expiryDate: order.expiryDate ? new Date(order.expiryDate) : null,
});

async function getOrders(
    resellerId: number,
    from: Date,
    to: Date,
    query: string,
    prevDate: Date,
    prevOrder: string,
    rows: number
): Promise<ResellerOrder[]> {
    const path = `/resellers/${resellerId}/orders?from=${from ? from.toISOString() : ''}&to=${
        to ? to.toISOString() : ''
    }&query=${query}&prevdate=${prevDate.toISOString()}&prevorder=${prevOrder}&rows=${rows}`;
    const result = await http<ResellerOrderResponse[]>({
        path: path,
    });
    if (result.ok && result.body) {
        return result.body.map(mapResellerOrder);
    } else {
        return [];
    }
}

async function getOrdersCount(resellerId: number, from: Date, to: Date, query: string): Promise<number> {
    const path = `/resellers/${resellerId}/orders/count?from=${from ? from.toISOString() : ''}&to=${
        to ? to.toISOString() : ''
    }&query=${query}`;
    const result = await http<ResellerOrderCount>({
        path: path,
    });
    if (result.ok && result.body) {
        return result.body.count;
    } else {
        return 0;
    }
}

async function getProducts(resellerId: number): Promise<ResellerProduct[]> {
    const path = `/resellers/${resellerId}/products`;
    const result = await http<ResellerProduct[]>({
        path: path,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return [];
    }
}

async function getProductPrice(pid: number, lic: number, qty: number): Promise<ResellerProductPrice | null> {
    const path = `/resellers/product-price?pid=${pid}&lic=${lic}&qty=${qty}`;
    const result = await http<ResellerProductPrice>({
        path: path,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return null;
    }
}

async function getCountries(): Promise<Country[]> {
    const path = `/resellers/countries`;
    const result = await http<Country[]>({
        path: path,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return [];
    }
}

async function getCheckoutInfo(resellerId: number, subtotal: number): Promise<ResellerCheckoutInfo | null> {
    const path = `/resellers/${resellerId}/checkout?subtotal=${subtotal}`;
    const result = await http<ResellerCheckoutInfo>({
        path: path,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return null;
    }
}

// Authenticated reseller checkout-session creation. Posts to
// /resellers/checkout (Authorize Reseller). The server pulls ResellerId
// from the JWT claim and applies per-line discounts from
// ResellerService.GetProducts — caller does not (and cannot) supply
// ResellerId or DiscountCode for this purpose.
async function createCheckout(params: CheckoutCreateRequest): Promise<HttpResponse<CheckoutCreateResult>> {
    return await http<CheckoutCreateResult, CheckoutCreateRequest>({
        path: `/resellers/checkout`,
        method: 'POST',
        body: params,
    });
}

async function checkout(params: any): Promise<CheckoutResponse | null> {
    const result = await http<CheckoutResponse, any>({
        path: `${secureServer}/OrderProcessBackend.aspx`,
        method: 'POST',
        body: params,
        formData: true,
        skipAuth: true,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return null;
    }
}

async function getQuoteCheckoutInfo(orderId: string): Promise<HttpResponse<QuoteCheckoutInfo>> {
    const path = `/resellers/quote-checkout/${orderId}`;
    const result = await http<QuoteCheckoutInfo>({
        path: path,
    });
    return result;
}

async function getExtraSupportInfo(orderId: string): Promise<HttpResponse<ResellerExtraSupport>> {
    const path = `/resellers/extra-support/${orderId}`;
    const result = await http<ResellerExtraSupport>({
        path: path,
    });

    if (result.ok && result.body) {
        return { ok: result.ok, message: result.message, body: result.body };
    } else {
        return { ok: false, message: result.message, body: null };
    }
}
